import React, { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { resetScrollToTop } from '../../../utils/scrollUtils';
import { primaryText, semiBoldText, xxLargeTextSize } from '../../../styling/stylingHelper';
import {
  COOKIES_POLICY,
  CULINARY_CLOUD_PRIVACY_POLICY,
  LegalDoc,
  TERMS_AND_CONDITIONS,
} from './legalContent';

const DOCS: Record<string, { doc: LegalDoc; titleKey: string }> = {
  'terms-and-conditions': { doc: TERMS_AND_CONDITIONS, titleKey: 'settings.termsAndConditions' },
  'privacy-policy': { doc: CULINARY_CLOUD_PRIVACY_POLICY, titleKey: 'settings.privacyPolicy' },
  'cookies-policy': { doc: COOKIES_POLICY, titleKey: 'settings.cookiesPolicy' },
};

// In-app version of the legal pages, reached from the settings page. Renders the
// same legalContent constants as the public landing pages, but inside the app
// layout so dark/light mode and navigation stay consistent.
const LegalDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { docKey } = useParams<{ docKey: string }>();

  // Start at the top when opening a policy (settings page may be scrolled down)
  useEffect(() => {
    resetScrollToTop();
  }, []);

  const entry = docKey ? DOCS[docKey] : undefined;
  if (!entry) {
    return <Navigate to="/settings" replace />;
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <ArrowLeft
            className="w-6 h-6 mr-2 cursor-pointer text-text dark:text-text-dark"
            onClick={() => navigate('/settings')}
          />
          <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText)}>{t(entry.titleKey)}</h1>
        </div>
      </div>

      <div className="bg-card dark:bg-card-dark shadow-lg rounded-2xl p-6 space-y-6">
        {entry.doc.sections.map((section, i) => (
          <section key={i}>
            {section.heading && (
              <h2 className="text-lg font-semibold mb-2 text-text dark:text-text-dark">
                {section.heading}
              </h2>
            )}
            {section.paragraphs?.map((paragraph, j) => (
              <p
                key={j}
                className={cn(
                  'text-sm text-muted dark:text-muted-dark leading-relaxed whitespace-pre-line',
                  j > 0 && 'mt-3'
                )}
              >
                {paragraph}
              </p>
            ))}
            {section.bullets && (
              <ul className="list-disc list-inside text-sm text-muted dark:text-muted-dark leading-relaxed space-y-1.5">
                {section.bullets.map((bullet, j) => (
                  <li key={j}>{bullet}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};

export default LegalDetailPage;
